import {
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  MatchLevel,
  PresetExtractor,
} from 'dashboard/models/document';
import { DocumentExtractionCheckConfig } from 'dashboard/models/verification-check-config/document-extracted-properties-detection';
import {
  DocumentType,
  DocumentTypeCheckConfig,
} from 'dashboard/models/verification-check-config/document-type-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

interface DocumentVerificationTemplateVersionConfigOverride {
  'verification-checks': VerificationCheckConfig[];
  extractions: ExtractionRequest[];
}

const SOCIAL_SECURITY_CARD_RECOMMENDED_CONFIG: DocumentVerificationTemplateVersionConfigOverride = {
  'verification-checks': [
    {
      check: CheckName.DocumentTypeDetection,
      'on-failed': VerificationStatus.Failed,
      'toggle-option': 'enabled',
      'type-acceptance-criteria': [],
      'type-rejection-criteria': [],
      // todo(danielaraujo): move to acceptance criteria
      'accepted-document-types': [
        DocumentType.SocialSecurityCardUnrestricted,
        DocumentType.SocialSecurityCardRestricted,
      ],
    } as DocumentTypeCheckConfig,
    {
      check: CheckName.DocumentExtractedPropertiesDetection,
      'on-failed': VerificationStatus.Failed,
      'toggle-option': 'enabled',
      'required-extractions': [
        {
          name: 'name',
          'field-name': 'name',
          'accept-condition': {
            '!==': [
              {
                var: 'accepts',
              },
              [],
            ] as ([] | { var: string })[],
          },
        },
        {
          name: 'social_security_number',
          'field-name': 'social_security_number',
          'accept-condition': {
            '!==': [
              {
                var: 'accepts',
              },
              [],
            ] as ([] | { var: string })[],
          },
        },
      ],
    } as DocumentExtractionCheckConfig,
    {
      check: CheckName.DocumentCompromisedDetection,
      'on-failed': VerificationStatus.Failed,
      'toggle-option': 'enabled',
    },
    {
      check: CheckName.DocumentTamperDetection,
      'on-failed': VerificationStatus.Failed,
      'toggle-option': 'enabled',
    },
    {
      check: CheckName.DocumentUnprocessableSubmissionDetection,
      'on-failed': VerificationStatus.Failed,
      'toggle-option': 'enabled',
    },
  ],
  extractions: [
    {
      version: '2.0',
      'extraction-type': ExtractionType.Guided,
      'field-type': ExtractionFieldType.NameFull,
      'field-name': 'name',
      'edit-distance-maximum': 0,
      'match-level-minimum': MatchLevel.Partial,
    },
    {
      version: '2.0',
      'extraction-type': ExtractionType.Unguided,
      'field-type': ExtractionFieldType.String,
      'field-name': 'social_security_number',
      'extractor-type': ExtractorType.Preset,
      'preset-extractor': PresetExtractor.Ssn,
    },
  ],
};

export const DOCUMENT_TYPE_TO_RECOMMENDED_CONFIG: Record<
  DocumentType,
  DocumentVerificationTemplateVersionConfigOverride
> = {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.AdditionalUtilityBill]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.ArticlesOfIncorporation]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.BankStatement]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.BankVoucher]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.BirthCertificate]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.BrokerageAccountStatement]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.BusinessLicense]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CertificateOfAuthorization]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CertificateOfCitizenship]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CertificateOfNaturalization]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Check]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CommercialLeaseAgreement]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.ConstanciaDeSituacionFiscal]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CreditCardStatement]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.DdForm214]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1040]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098C]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098E]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098F]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098Ma]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098Q]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1098T]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099Consolidated]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099B]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099Div]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099Int]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099K]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099Misc]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Form1099Nec]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.FormW2]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.FormW8BenE]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.FormW8Eci]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.FormW9]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.HealthInsuranceCard]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.HomeownersInsuranceStatement]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.IndependentThirdPartyAmlAssessment]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Invoice]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Irs147cLetter]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.IrsCp575Letter]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.LetterOfAuthorization]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.NgbForm22]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.NoticeOfAssessment]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Paystub]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Receipt]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.RentersInsuranceStatement]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.ReportOfBirthAbroad]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.ResidentialLeaseAgreement]: null,

  [DocumentType.SocialSecurityCardUnrestricted]: SOCIAL_SECURITY_CARD_RECOMMENDED_CONFIG,
  [DocumentType.SocialSecurityCardRestricted]: SOCIAL_SECURITY_CARD_RECOMMENDED_CONFIG,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.StandardUtilityBill]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.StudentIdCard]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.TrustAgreement]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.VehicleInsurance]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.VehicleRegistration]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.VehicleTitle]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.GovernmentId]: null,

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.Other]: null,

  // LEGACY TYPES / TO BE DEPRECATED
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.CorporateDocument]: null,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  [DocumentType.TaxDocument]: null,
};
